import { useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'necro-turn-notifications-enabled';

const getInitialEnabled = () => {
  if (typeof window === 'undefined') return true;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return true;
  return raw === 'true';
};

const getPermission = () => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported';
  }
  return Notification.permission;
};

const isLikelyMobileBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = `${navigator.userAgent || ''}`.toLowerCase();
  return /android|iphone|ipad|ipod|mobile/.test(ua);
};

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  const mediaStandalone = typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
  return Boolean(mediaStandalone || iosStandalone);
};

const runMobileFallbackAlert = ({ title }) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate([220, 120, 220, 120, 320]);
  }

  const originalTitle = document.title;
  const pulseTitle = `⚡ ${title}`;
  let tick = 0;
  const maxTicks = 10;
  const timer = window.setInterval(() => {
    document.title = tick % 2 === 0 ? pulseTitle : originalTitle;
    tick += 1;
    if (tick >= maxTicks) {
      window.clearInterval(timer);
      document.title = originalTitle;
    }
  }, 850);
};

const useTurnNotifications = () => {
  const [enabled, setEnabled] = useState(getInitialEnabled);
  const [permission, setPermission] = useState(getPermission);
  const mobileBrowser = isLikelyMobileBrowser();
  const standalone = isStandaloneDisplay();

  const supported = permission !== 'unsupported';

  const setNotificationsEnabled = useCallback((value) => {
    setEnabled(Boolean(value));
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supported) return 'unsupported';
    if (Notification.permission === 'granted') {
      setPermission('granted');
      return 'granted';
    }

    const next = await Notification.requestPermission();
    setPermission(next);
    return next;
  }, [supported]);

  const sendTurnNotification = useCallback(({ title, body, tag, targetPath }) => {
    if (!enabled) return null;

    const hidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
    if (supported && permission === 'granted' && hidden) {
      const notice = new Notification(title, {
        body,
        tag,
        renotify: true,
        requireInteraction: false,
      });

      if (targetPath) {
        notice.onclick = () => {
          if (typeof window !== 'undefined') {
            window.focus();
            window.location.assign(targetPath);
          }
        };
      }

      return notice;
    }

    // Mobile browsers can block prompts/notifications in tab mode; fallback with vibration + title pulse.
    if (mobileBrowser) {
      runMobileFallbackAlert({ title });
    }

    return null;
  }, [enabled, mobileBrowser, permission, supported]);

  const triggerTestAlert = useCallback(() => {
    const title = 'Necronomicon: Turn alert test';

    if (supported && permission === 'granted') {
      const notice = new Notification(title, {
        body: 'Test notification from the Lounge alert panel.',
        tag: `turn-test-${Date.now()}`,
      });
      notice.onclick = () => {
        if (typeof window !== 'undefined') {
          window.focus();
        }
      };
      return true;
    }

    if (mobileBrowser) {
      runMobileFallbackAlert({ title });
      return true;
    }

    return false;
  }, [mobileBrowser, permission, supported]);

  const statusLabel = useMemo(() => {
    if (!supported) return 'Native browser notifications are unavailable here. Mobile fallback alerts are still enabled.';
    if (!enabled) return 'Turn alerts are off.';
    if (permission === 'denied') return 'Browser blocked notifications. Enable them in browser settings.';
    if (mobileBrowser && permission !== 'granted' && !standalone) {
      return 'On mobile, Chrome may block notification prompts in browser tab mode. Install to home screen for stronger turn alerts.';
    }
    if (permission === 'default') return 'Turn alerts are ready. Click enable to allow browser notifications.';
    return 'Turn alerts are on.';
  }, [enabled, mobileBrowser, permission, standalone, supported]);

  return {
    supported,
    enabled,
    permission,
    mobileBrowser,
    standalone,
    statusLabel,
    setNotificationsEnabled,
    requestPermission,
    sendTurnNotification,
    triggerTestAlert,
  };
};

export default useTurnNotifications;
